import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Eye, EyeOff, AlertCircle } from 'lucide-react'
import QuestionViewer from '../components/QuestionViewer'
import QuestionListItem from '../components/QuestionListItem'
import type { QuestionResponse } from '../types'
import { useReEngagement } from '@/features/payments'
import { fbTrackPYQView } from '@/shared/utils/facebookPixel'
import { useMediaQuery } from '@/shared/hooks/useMediaQuery'

interface LocationState {
  questions: QuestionResponse[]
  testCode: string
  examLabel: string
  /** Set when a phone user opened a question from the list (so Back can pop history) */
  fromList?: boolean
}

/**
 * Gmail-style PYQ browser.
 * - Tablet/desktop (md+): question list on the left, reading pane on the right.
 * - Phone: list only; tapping a question opens it as its own view (Back returns to the list).
 * The open question lives in the URL as ?q=<1-based number>.
 */
export default function PYQExamBrowserPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as LocationState | null
  const isWide = useMediaQuery('(min-width: 768px)')
  const [showSolution, setShowSolution] = useState(false)
  const listRef = useRef<HTMLUListElement>(null)

  const questions = state?.questions || []
  const examLabel = state?.examLabel || 'NEET PYQ'
  const testCode = state?.testCode || ''
  const totalQuestions = questions.length

  const qParam = Number(searchParams.get('q'))
  const hasSelection = Number.isInteger(qParam) && qParam >= 1 && qParam <= totalQuestions
  // Wide screens always show a question (the first by default); phones show the list until one is opened
  const selectedIndex = hasSelection ? qParam - 1 : isWide && totalQuestions > 0 ? 0 : null
  const currentQuestion = selectedIndex !== null ? questions[selectedIndex] : undefined
  const showList = isWide || selectedIndex === null

  const selectQuestion = useCallback(
    (index: number) => {
      // Phone list -> question is a new view (own history entry); everything else just swaps the question
      const opensNewView = !isWide && !hasSelection
      navigate(
        { search: `?q=${index + 1}` },
        { state: { ...state, fromList: opensNewView || state?.fromList }, replace: !opensNewView }
      )
    },
    [isWide, hasSelection, navigate, state]
  )

  const backToList = () => {
    if (state?.fromList) {
      navigate(-1)
    } else {
      navigate({ search: '' }, { state: { ...state, fromList: false }, replace: true })
    }
  }

  const goToPrev = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) selectQuestion(selectedIndex - 1)
  }, [selectedIndex, selectQuestion])

  const goToNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < totalQuestions - 1) selectQuestion(selectedIndex + 1)
  }, [selectedIndex, totalQuestions, selectQuestion])

  // Track ViewContent for FB Pixel when exam is loaded
  useEffect(() => {
    if (testCode && totalQuestions > 0) {
      fbTrackPYQView('exam', testCode, examLabel)
    }
  }, [testCode, examLabel, totalQuestions])

  const { saveProgress } = useReEngagement()

  // Save progress when question changes
  useEffect(() => {
    if (testCode && selectedIndex !== null) {
      saveProgress(testCode, selectedIndex)
    }
  }, [testCode, selectedIndex, saveProgress])

  // Reset solution visibility when question changes
  useEffect(() => {
    setShowSolution(false)
  }, [selectedIndex])

  // Keep the selected row visible in the list pane
  useEffect(() => {
    if (!isWide || selectedIndex === null) return
    listRef.current
      ?.querySelector(`[data-question-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [isWide, selectedIndex])

  // Gmail-style keyboard navigation: j = next, k = previous
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'j') goToNext()
      if (e.key === 'k') goToPrev()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [goToNext, goToPrev])

  const goBack = () => navigate('/question-bank/by-exam')

  // No questions state
  if (!state || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800">No questions loaded</h2>
          <p className="text-gray-600 mt-1 mb-4">
            Please select an exam to browse questions.
          </p>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Select Exam
          </button>
        </div>
      </div>
    )
  }

  return (
    // `relative` on the scroll containers keeps KaTeX's absolutely-positioned hidden
    // MathML clipped inside them; otherwise it stretches the page and adds a window scrollbar
    <div className="relative h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Page header (hidden on phones while a question is open) */}
      {showList && (
        <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={goBack}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-gray-900">{examLabel}</h1>
              <p className="text-xs text-gray-500">{totalQuestions} Questions</p>
            </div>
          </div>
        </header>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Question list */}
        {showList && (
          <aside
            className="relative w-full overflow-y-auto bg-white md:w-80 md:flex-shrink-0 md:border-r md:border-gray-200 lg:w-96"
            aria-label="Questions"
          >
            <ul ref={listRef}>
              {questions.map((question, index) => (
                <QuestionListItem
                  key={question.questionId}
                  question={question}
                  questionNumber={index + 1}
                  selected={index === selectedIndex}
                  onSelect={selectQuestion}
                />
              ))}
            </ul>
          </aside>
        )}

        {/* Reading pane */}
        {currentQuestion && selectedIndex !== null && (
          <section className="flex flex-1 min-w-0 flex-col bg-white">
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3 py-2">
              {!isWide && (
                <>
                  <button
                    onClick={backToList}
                    aria-label="Back to questions"
                    className="rounded-full p-2 text-gray-600 hover:bg-gray-100"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <span className="min-w-0 truncate text-sm font-medium text-gray-700">{examLabel}</span>
                </>
              )}

              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={() => setShowSolution((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    showSolution
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showSolution ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  <span>{showSolution ? 'Hide' : 'Solution'}</span>
                </button>
                <span className="hidden px-2 text-sm text-gray-500 sm:inline">
                  {selectedIndex + 1} of {totalQuestions}
                </span>
                <button
                  onClick={goToPrev}
                  disabled={selectedIndex === 0}
                  aria-label="Previous question"
                  className="rounded-full p-2 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={selectedIndex >= totalQuestions - 1}
                  aria-label="Next question"
                  className="rounded-full p-2 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <QuestionViewer
                question={currentQuestion}
                questionNumber={selectedIndex + 1}
                totalQuestions={totalQuestions}
                showSolution={showSolution}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  )
}
